import { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getAllInstructorPayments, processInstructorPayment } from "@/utils/firebase";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { DollarSign, Clock, Users, X, Check } from "lucide-react";

interface InstructorPayment {
  id: string;
  instructorId: string;
  instructorName: string;
  studentId: string;
  studentName: string;
  enrollmentId: string;
  courseId: string;
  courseName: string;
  totalAmount: number;
  amountPaid: number;
  amountRemaining: number;
  totalHours: number;
  hoursTaught: number;
  hoursRemaining: number;
  currency: string;
  paymentStatus: 'unpaid' | 'partially_paid' | 'fully_paid';
  lastPaymentDate?: string;
  createdAt: string;
  updatedAt: string;
}

const InstructorPaymentsManagement = () => {
  const [payments, setPayments] = useState<InstructorPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [instructors, setInstructors] = useState<{[key: string]: {name: string, totalEarned: number, totalPending: number}}>({}); 
  const [selectedInstructor, setSelectedInstructor] = useState<string>("");
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<InstructorPayment | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const { toast } = useToast();
  
  useEffect(() => {
    fetchPayments();
  }, []);
  
  const fetchPayments = async () => {
    setLoading(true);
    try {
      const fetchedPayments = await getAllInstructorPayments();
      console.log("Fetched all instructor payments:", fetchedPayments);
      setPayments(fetchedPayments);
      
      // Process instructor summaries
      const instructorSummary: {[key: string]: {name: string, totalEarned: number, totalPending: number}} = {};
      
      fetchedPayments.forEach(payment => {
        if (!instructorSummary[payment.instructorId]) {
          instructorSummary[payment.instructorId] = {
            name: payment.instructorName,
            totalEarned: 0,
            totalPending: 0
          };
        }
        
        instructorSummary[payment.instructorId].totalEarned += payment.amountPaid;
        instructorSummary[payment.instructorId].totalPending += payment.amountRemaining;
      });
      
      setInstructors(instructorSummary);
    } catch (error) {
      console.error("Error fetching instructor payments:", error);
      toast({
        title: "Error",
        description: "Could not load instructor payments",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleProcessPayment = async () => {
    if (!selectedPayment || paymentAmount <= 0) return;
    
    try {
      // Validate payment amount
      if (paymentAmount > selectedPayment.amountRemaining) {
        toast({
          title: "Invalid Amount",
          description: "Payment amount cannot exceed remaining balance",
          variant: "destructive"
        });
        return;
      }

      // Process the payment
      await processInstructorPayment(selectedPayment.id, paymentAmount);
      
      // Update local state
      const updatedPayments = payments.map(p => {
        if (p.id === selectedPayment.id) {
          const newAmountPaid = p.amountPaid + paymentAmount;
          const newAmountRemaining = p.totalAmount - newAmountPaid;
          return {
            ...p,
            amountPaid: newAmountPaid,
            amountRemaining: newAmountRemaining,
            paymentStatus: newAmountRemaining <= 0 ? 'fully_paid' as const : 'partially_paid' as const,
            lastPaymentDate: new Date().toISOString()
          };
        }
        return p;
      });
      
      setPayments(updatedPayments);
      
      toast({
        title: "Payment Processed",
        description: `Successfully processed payment of ${formatCurrency(paymentAmount)} to ${selectedPayment.instructorName}`,
      });
      
      setPaymentDialogOpen(false);
    } catch (error) {
      console.error("Error processing payment:", error);
      toast({
        title: "Payment Failed",
        description: "Could not process instructor payment",
        variant: "destructive"
      });
    }
  };
  
  const openPaymentDialog = (payment: InstructorPayment) => {
    setSelectedPayment(payment);
    // Default to the remaining amount or 0 if fully paid
    setPaymentAmount(payment.amountRemaining);
    setPaymentDialogOpen(true);
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'fully_paid':
        return <Badge className="bg-green-500">Fully Paid</Badge>;
      case 'partially_paid':
        return <Badge className="bg-yellow-500">Partially Paid</Badge>;
      case 'unpaid':
      default:
        return <Badge className="bg-gray-500">Unpaid</Badge>;
    }
  };
  
  const filteredPayments = payments
    .filter(p => activeTab === "all" || p.paymentStatus === activeTab)
    .filter(p => !selectedInstructor || p.instructorId === selectedInstructor);
  
  const totalPaid = payments.reduce((sum, p) => sum + p.amountPaid, 0);
  const totalPending = payments.reduce((sum, p) => sum + p.amountRemaining, 0);
  
  // Calculate instructor summaries with more details
  const instructorSummaries = payments.reduce((acc, payment) => {
    if (!acc[payment.instructorId]) {
      acc[payment.instructorId] = {
        name: payment.instructorName,
        totalEarned: 0,
        totalPending: 0,
        totalHoursTaught: 0,
        totalHoursAssigned: 0,
        paymentRecords: 0
      };
    }
    
    acc[payment.instructorId].totalEarned += payment.amountPaid;
    acc[payment.instructorId].totalPending += payment.amountRemaining;
    acc[payment.instructorId].totalHoursTaught += payment.hoursTaught;
    acc[payment.instructorId].totalHoursAssigned += payment.totalHours;
    acc[payment.instructorId].paymentRecords += 1;
    
    return acc;
  }, {} as {[key: string]: {
    name: string;
    totalEarned: number;
    totalPending: number;
    totalHoursTaught: number;
    totalHoursAssigned: number;
    paymentRecords: number;
  }});
  
  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Instructor Payments Management</h1>
          <p className="text-gray-500 mt-2">Manage and process instructor payments</p>
        </div>
        
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="mr-4 bg-green-100 p-3 rounded-full">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Paid to Instructors</p>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {loading ? "Loading..." : formatCurrency(totalPaid)}
                  </h3>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="mr-4 bg-amber-100 p-3 rounded-full">
                  <DollarSign className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Pending Payments</p>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {loading ? "Loading..." : formatCurrency(totalPending)}
                  </h3>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="mr-4 bg-blue-100 p-3 rounded-full">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Instructors</p>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {loading ? "Loading..." : Object.keys(instructorSummaries).length}
                  </h3>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="mr-4 bg-purple-100 p-3 rounded-full">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Hours Taught</p>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {loading ? "Loading..." : payments.reduce((sum, p) => sum + p.hoursTaught, 0)}
                  </h3>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Instructor Payment Records</CardTitle>
            <CardDescription>
              View and manage all instructor payment records
            </CardDescription>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mt-4">
              <div className="w-full sm:w-64">
                <Label htmlFor="instructor-filter">Filter by Instructor</Label>
                <select
                  id="instructor-filter"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={selectedInstructor}
                  onChange={(e) => setSelectedInstructor(e.target.value)}
                >
                  <option value="">All Instructors</option>
                  {Object.entries(instructorSummaries).map(([id, data]) => (
                    <option key={id} value={id}>
                      {data.name} ({formatCurrency(data.totalPending)} pending)
                    </option>
                  ))}
                </select>
              </div>
              
              <Button 
                variant="outline" 
                onClick={() => {
                  setSelectedInstructor("");
                  setActiveTab("all");
                }}
              >
                Reset Filters
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All Records</TabsTrigger>
                <TabsTrigger value="unpaid">Unpaid</TabsTrigger>
                <TabsTrigger value="partially_paid">Partially Paid</TabsTrigger>
                <TabsTrigger value="fully_paid">Fully Paid</TabsTrigger>
              </TabsList>
              
              {loading ? (
                <div className="text-center py-10">
                  <p>Loading payment records...</p>
                </div>
              ) : filteredPayments.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium">No payment records found</h3>
                  <p className="text-gray-500 mt-2">
                    {activeTab === "all" 
                      ? "There are no instructor payment records yet" 
                      : `No ${activeTab.replace('_', ' ')} payment records found`}
                  </p>
                </div>
              ) : (
                <div className="border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Instructor</TableHead>
                        <TableHead>Student</TableHead>
                        <TableHead>Course</TableHead>
                        <TableHead>Hours</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last Updated</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPayments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>{payment.instructorName}</TableCell>
                          <TableCell>{payment.studentName}</TableCell>
                          <TableCell>{payment.courseName}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{payment.hoursTaught} / {payment.totalHours} hrs</div>
                              <div className="text-gray-500">
                                {payment.hoursRemaining} hrs remaining
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{formatCurrency(payment.amountPaid)}</div>
                              <div className="text-gray-500">
                                of {formatCurrency(payment.totalAmount)}
                              </div>
                              <div className="text-xs text-amber-600">
                                {formatCurrency(payment.amountRemaining)} remaining
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getPaymentStatusBadge(payment.paymentStatus)}</TableCell>
                          <TableCell>
                            {payment.lastPaymentDate 
                              ? format(new Date(payment.lastPaymentDate), "MMM d, yyyy") 
                              : format(new Date(payment.createdAt), "MMM d, yyyy")}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openPaymentDialog(payment)}
                              disabled={payment.paymentStatus === 'fully_paid' || payment.amountRemaining <= 0}
                            >
                              Process Payment
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Tabs>
          </CardContent>
        </Card>
      </div>
      
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Instructor Payment</DialogTitle>
            <DialogDescription>
              Make a payment to {selectedPayment?.instructorName} for {selectedPayment?.courseName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="payment-details">Payment Details</Label>
              <div className="text-sm" id="payment-details">
                <p><strong>Student:</strong> {selectedPayment?.studentName}</p>
                <p><strong>Course:</strong> {selectedPayment?.courseName}</p>
                <p><strong>Hours Taught:</strong> {selectedPayment?.hoursTaught} / {selectedPayment?.totalHours}</p>
                <p><strong>Total Due:</strong> {selectedPayment ? formatCurrency(selectedPayment.totalAmount) : '--'}</p>
                <p><strong>Already Paid:</strong> {selectedPayment ? formatCurrency(selectedPayment.amountPaid) : '--'}</p>
                <p><strong>Remaining:</strong> {selectedPayment ? formatCurrency(selectedPayment.amountRemaining) : '--'}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="payment-amount">Payment Amount</Label>
              <div className="flex items-center">
                <span className="bg-gray-100 border border-gray-300 rounded-l-md px-3 py-2">€</span>
                <Input 
                  id="payment-amount" 
                  type="number"
                  min="0"
                  step="0.01"
                  max={selectedPayment?.amountRemaining || 0}
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                  className="rounded-l-none"
                />
              </div>
              {paymentAmount > (selectedPayment?.amountRemaining || 0) && (
                <p className="text-red-500 text-sm">Amount exceeds remaining balance</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button 
              onClick={handleProcessPayment}
              disabled={paymentAmount <= 0 || paymentAmount > (selectedPayment?.amountRemaining || 0)}
            >
              <Check className="h-4 w-4 mr-2" />
              Process Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default InstructorPaymentsManagement;
